/**
 * Odoo endpoint shim — answers gridOrder.bundle.js's calls to the legacy
 * shakawear.odoo.com /shopify/* API using Storefront product metadata and
 * the signed, same-origin warehouse inventory app proxy.
 * Must load BEFORE gridOrder.bundle.js. Response shapes mirror what the
 * bundle consumes (Admin-REST-style payloads wrapped in {result: ...}).
 */
(function () {
  'use strict';

  var ODOO_HOSTS = ['shakawear.odoo.com', 'shakawear-staging-shopify-api-donotdelete-18527473.dev.odoo.com'];
  var SF_URL = 'https://shakawear.myshopify.com/api/2026-07/graphql.json';
  var SF_TOKEN = 'e81e3d65c0b1cec20f9c3f4c1cb76c5a'; // public Storefront token (already shipped in cart-template.liquid)

  // numeric variant id -> [{location_id, available}] from the warehouse proxy
  var availabilityByVariant = {};

  function gidNum(gid) {
    return parseInt(String(gid).split('/').pop(), 10);
  }

  function sfQuery(query, variables) {
    return realFetch(SF_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': SF_TOKEN,
      },
      body: JSON.stringify({query: query, variables: variables || {}}),
    }).then(function (r) {
      if (!r.ok) throw requestError(502);
      return r.json();
    }).then(function (res) {
      if (!res || (res.errors && res.errors.length)) throw requestError(502);
      return res;
    });
  }

  function jsonResponse(obj, status) {
    return new Response(JSON.stringify(obj), {
      status: status || 200,
      headers: {'Content-Type': 'application/json'},
    });
  }

  function requestError(status) {
    var error = new Error('Warehouse data unavailable');
    error.status = status;
    return error;
  }

  function warehouseQuery(variantIds) {
    var ids = variantIds || [];
    var url = '/apps/warehouse-inventory';
    if (ids.length) url += '?variant_ids=' + encodeURIComponent(ids.join(','));
    ids.forEach(function (id) { delete availabilityByVariant[id]; });
    return realFetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {'Accept': 'application/json'},
    }).then(function (r) {
      if (!r.ok) throw requestError(r.status >= 400 && r.status <= 599 ? r.status : 502);
      return r.json();
    }).then(function (res) {
      if (!res || !Array.isArray(res.locations) || !res.variants || typeof res.variants !== 'object') {
        throw requestError(502);
      }
      var locationIds = res.locations.map(function (location) {
        if (!location || !Number.isSafeInteger(location.id) || location.id <= 0 || location.active !== true) {
          throw requestError(502);
        }
        return location.id;
      });
      ids.forEach(function (id) {
        var levels = res.variants[id];
        if (!Array.isArray(levels) || levels.length !== locationIds.length) throw requestError(502);
        var seen = {};
        levels.forEach(function (level) {
          if (!level || locationIds.indexOf(level.location_id) === -1 || seen[level.location_id] ||
              !Number.isSafeInteger(level.available)) {
            throw requestError(502);
          }
          seen[level.location_id] = true;
        });
      });
      // Only a complete, successful response may populate the grid's cache.
      ids.forEach(function (id) { availabilityByVariant[id] = res.variants[id]; });
      return res;
    });
  }

  function parseParams(init) {
    try {
      return (JSON.parse(init.body) || {}).params || {};
    } catch (e) {
      return {};
    }
  }

  function handleAccessToken() {
    // The bundle stores the token but never sends it anywhere.
    return Promise.resolve(jsonResponse({result: {'access-token': 'storefront-shim'}}));
  }

  function handleLocations() {
    return warehouseQuery().then(function (res) {
      return jsonResponse({result: {locations: res.locations}});
    });
  }

  function handleProduct(params) {
    var q = 'query($id: ID!) { product(id: $id) { id title variants(first: 250) { nodes { id title sku quantityAvailable price { amount } selectedOptions { value } } } } }';
    return sfQuery(q, {id: 'gid://shopify/Product/' + params.product_id}).then(function (res) {
      var product = res.data && res.data.product;
      if (!product) throw requestError(404);
      if (!product.variants || !Array.isArray(product.variants.nodes)) throw requestError(502);
      var variants = product.variants.nodes.map(function (v) {
        var num = gidNum(v.id);
        var opts = v.selectedOptions || [];
        return {
          id: num,
          title: v.title,
          sku: v.sku,
          option1: opts[0] ? opts[0].value : null,
          option2: opts[1] ? opts[1].value : null,
          option3: opts[2] ? opts[2].value : null,
          price: Number(v.price.amount).toFixed(2),
          inventory_item_id: num, // synthetic: keys the inventory-levels lookup
          inventory_quantity: v.quantityAvailable,
        };
      });
      var inventory = variants.length ? warehouseQuery(variants.map(function (v) { return v.id; })) : Promise.resolve();
      return inventory.then(function () {
        return jsonResponse({result: {product: {id: Number(params.product_id), title: product.title, variants: variants}}});
      });
    });
  }

  // Cart page: {result: {variant: {inventory_item_id}}} — synthetic id = variant id
  function handleProductVariant(params) {
    return Promise.resolve(jsonResponse({result: {variant: {inventory_item_id: Number(params.variant_id)}}}));
  }

  // Cart page: {result: {inventory_items: {<id>: [{location_id, available}]}}}
  function handleInventoryItems(params) {
    var ids = String(params.inventory_item_ids || '').split(',').map(Number);
    // Cart inventory must be fresh, even if the grid already cached these ids.
    return warehouseQuery(ids).then(function (res) {
      return jsonResponse({result: {inventory_items: res.variants}});
    });
  }

  function handleInventoryLevels(params) {
    var ids = String(params.inventory_item_ids || '').split(',').map(Number);
    var locationId = Number(params.location_ids);
    var missingIds = ids.filter(function (id) { return !availabilityByVariant[id]; });
    var inventory = missingIds.length ? warehouseQuery(missingIds) : Promise.resolve();
    return inventory.then(function () {
      var levels = ids.map(function (id) {
        var perLocation = availabilityByVariant[id];
        var match = perLocation.filter(function (l) { return l.location_id === locationId; })[0];
        return {
          inventory_item_id: id,
          location_id: locationId,
          available: match ? match.available : 0,
        };
      });
      return jsonResponse({result: {inventory_levels: levels}});
    });
  }

  var realFetch = window.fetch.bind(window);

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var isOdoo = ODOO_HOSTS.some(function (h) { return url.indexOf(h) !== -1; });
    if (!isOdoo) return realFetch(input, init);

    var params = parseParams(init || {});
    var shim;
    if (url.indexOf('/shopify/access-token') !== -1) shim = handleAccessToken();
    else if (url.indexOf('/shopify/inventory-levels/locations') !== -1) shim = handleInventoryLevels(params);
    else if (url.indexOf('/shopify/inventory-levels') !== -1) shim = handleInventoryItems(params);
    else if (url.indexOf('/shopify/locations') !== -1) shim = handleLocations();
    else if (url.indexOf('/shopify/product-variant') !== -1) shim = handleProductVariant(params);
    else if (url.indexOf('/shopify/product') !== -1) shim = handleProduct(params);
    else return realFetch(input, init);

    return shim.catch(function (e) {
      return jsonResponse({error: 'Warehouse data unavailable'}, e && e.status || 502);
    });
  };
})();
